import React, { useEffect } from 'react';
import { ProductAccordionProvider } from './AccordionContext';
import RightSide from './rightSide/RightSide';
import LeftSide from './leftSide/LeftSide';
import { SmartModuleProvider } from './SmartModuleProvider';
import { ShopifyProductProvider, useShopifyProduct } from '../../../api/shopify/ShopifyProduct';
import { TrackingProvider } from '@jbhi-fi/components/src/Tracking';
import { useIdPrefix } from '@jbhi-fi/components/src/IdPrefix';
import { getInitialAccordionState } from './utils/utils';
import { styles } from './Layout.css';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { SmartModuleWrapper } from './smartModules/SmartModuleWrapper';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag/useFeatureFlag';
import { AlternativesSlideout } from '@jbhi-fi/components/src/ProductAlternatives/AlternativesSlideout';
import { Disclaimer } from '@jbhi-fi/components/src/Disclaimer';
import { ShopifyProductTransformer } from '@jbhi-fi/components/src/Shopify/transformers/ShopifyProductTransformer';

const LayoutWrapper = () => {
  return (
    <ShopifyProductProvider>
      <Layout />
    </ShopifyProductProvider>
  );
};

const Layout = (): JSX.Element => {
  const settings = window.smartModuleSettings;
  const smartModuleSettings = [
    { ...window.smartModuleSettings.pdpMiddle, location: 'pdpMiddle' as const },
    { ...window.smartModuleSettings.pdpBottom, location: 'pdpBottom' as const },
  ].filter(setting => setting?.isEnabled);

  const { shopify, marketplaceSellers } = useShopifyProduct();

  const isMarketplaceProduct =
    ShopifyProductTransformer.isMarketplaceProduct({ product: shopify }) && !!marketplaceSellers?.[0];

  const currentProduct = {
    sku: shopify.variants[0]?.sku,
    productGroup: window.Product.productGroup,
    optimizelyBundleOptions: window.Product.optimizelyBundleOptions,
    bundleOptions: window.Product.bundleOptions,
  };

  const initialAccordianState = getInitialAccordionState(window.Jbhifi.sku);
  const isMobile = useBreakpoint('md', 'down');

  useEffect(() => {
    const zendesk: HTMLElement | null = document.querySelector('body div iframe#launcher');
    if (zendesk && isMobile) {
      zendesk.style.bottom = '90px';
    }
  }, []);

  const elementIds = {
    layoutWrapper: useIdPrefix('layout-wrapper'),
    layoutInner: useIdPrefix('layout-inner'),
    layoutBasic: useIdPrefix('layout-basic'),
    layoutFull: useIdPrefix('layout-full'),
    rightPanel: useIdPrefix('right-panel'),
    leftPanel: useIdPrefix('left-panel'),
  };

  const isEnhancedGoogleRecommendationsEnabled = useFeatureFlag('isEnhancedGoogleRecommendationsEnabled');

  return (
    <SmartModuleProvider
      sourceProducts={[currentProduct]}
      options={{
        smartModuleSettings: [settings?.pdpBottom, settings?.pdpMiddle, settings?.pdpTop],
        googleAcceptedDeviationPercentage: settings?.googleAcceptedDeviationPercentage,
        isEnhancedGoogleRecommendationsEnabled: !!isEnhancedGoogleRecommendationsEnabled,
      }}
    >
      {isEnhancedGoogleRecommendationsEnabled || settings?.isPdpSimilarItemsEnabled ? <AlternativesSlideout /> : null}
      <ProductAccordionProvider initialActiveState={initialAccordianState}>
        <TrackingProvider category="pdp" context="pdp" trackPageLoad={false}>
          <div className={styles.layoutWrapper} id={elementIds.layoutWrapper}>
            <div className={styles.layout} id={elementIds.layoutInner}>
              <div className={styles.layoutRight} id={elementIds.rightPanel}>
                <RightSide />
              </div>
              <div className={styles.layoutLeft} id={elementIds.leftPanel}>
                <LeftSide />
              </div>
            </div>
          </div>
          <div className={styles.layoutBasic} id={elementIds.layoutBasic}>
            <div className={styles.layoutFull} id={elementIds.layoutFull}>
              {smartModuleSettings.map(setting => (
                <div
                  key={setting.type}
                  className={styles.pdpSmartModuleContainer}
                  data-testid={`pdp-full-${setting.type}-container`}
                >
                  <SmartModuleWrapper
                    settings={setting}
                    location={setting.location}
                    isMarketplaceProduct={isMarketplaceProduct}
                  />
                </div>
              ))}
              <div className={styles.disclaimerWrapper} data-testid="pdp-full-width-disclaimer-wrapper">
                <Disclaimer disclaimers={window.Jbhifi.disclaimers} />
              </div>
            </div>
          </div>
        </TrackingProvider>
      </ProductAccordionProvider>
    </SmartModuleProvider>
  );
};

export default LayoutWrapper;
